import api from '@/utils/api'
import type { User, PaginatedResponse, PaginationParams } from '@/types'
import type { UserWithActivity } from '@/types/user-statistics'

export interface CreateUserRequest {
  username: string
  password: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  roleId: number
  isActive?: boolean
}

export interface UpdateUserRequest {
  username?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  roleId?: number
  isActive?: boolean
}

export interface Role {
  id: number
  name: string
  description?: string
  permissions: string[]
  createdAt: string
  updatedAt: string
}

export interface CreateRoleRequest {
  name: string
  description?: string
  permissions: string[]
}

export interface UpdateRoleRequest {
  name?: string
  description?: string
  permissions?: string[]
}

export interface MergeUsersRequest {
  sourceUserId: number;
  targetUserId: number;
}

export const usersApi = {
  // 用户管理
  async getUsers(params: PaginationParams): Promise<User[]> {
    const response = await api.get('/users', { params })
    return response.data
  },

  async getUser(id: number): Promise<User> {
    const response = await api.get(`/users/${id}`)
    return response.data
  },

  // 合并用户账号
  async mergeUsers(data: MergeUsersRequest): Promise<User> {
    const response = await api.post('/users/merge', data)
    return response.data
  },

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await api.post('/users', userData)
    return response.data
  },

  async updateUser(id: number, updateData: UpdateUserRequest): Promise<User> {
    const response = await api.patch(`/users/${id}`, updateData)
    return response.data
  },

  async deleteUser(id: number): Promise<void> {
    await api.delete(`/users/${id}`)
  },

  async assignRole(userId: number, roleId: number): Promise<User> {
    const response = await api.patch(`/users/${userId}/role/${roleId}`)
    return response.data
  },

  async toggleUserStatus(id: number, isActive: boolean): Promise<User> {
    const response = await api.patch(`/users/${id}/toggle-active`, { isActive })
    return response.data
  },

  // 角色管理
  async getRoles(params?: PaginationParams): Promise<Role[]> {
    const response = await api.get('/roles', { params })
    return response.data
  },

  async getRole(id: number): Promise<Role> {
    const response = await api.get(`/roles/${id}`)
    return response.data
  },

  async createRole(roleData: CreateRoleRequest): Promise<Role> {
    const response = await api.post('/roles', roleData)
    return response.data
  },

  async updateRole(id: number, updateData: UpdateRoleRequest): Promise<Role> {
    const response = await api.patch(`/roles/${id}`, updateData)
    return response.data
  },

  async deleteRole(id: number): Promise<void> {
    await api.delete(`/roles/${id}`)
  },

  async getAvailablePermissions(): Promise<string[]> {
    // Backend doesn't have this endpoint yet, return hardcoded list for now
    return [
      'user:read',
      'user:create', 
      'user:update',
      'user:delete',
      'role:read',
      'role:create',
      'role:update',
      'role:delete',
      'questionBank:read',
      'questionBank:create',
      'questionBank:update',
      'questionBank:delete',
      'question:read',
      'question:create',
      'question:update',
      'question:delete',
      'quiz:read',
      'quiz:create',
      'quiz:update',
      'quiz:delete',
    ]
  },

  // 用户活跃统计
  async getUsersWithActivityStats(): Promise<UserWithActivity[]> {
    const response = await api.get('/users/with-activity-stats')
    return response.data
  },

  async getUserActivityStats(id: number): Promise<{
    totalQuestions: number;
    correctAnswers: number;
    correctRate: number;
    wrongQuestionCount: number;
    masteredWrongQuestions: number;
    totalSessions: number;
    lastActiveTime?: string;
    averageScore: number;
  }> {
    const response = await api.get(`/users/${id}/activity-stats`)
    return response.data
  },
}
